/**
 * DemoDeck — CaraLoom Controlled Demo Environment · Presenter Deck (iter 179).
 *
 * Print-optimized slide-deck version of the demo. Route: `/demo/preview/deck`.
 *
 * Each slide is a full-viewport section with `page-break-after: always` so a
 * browser "Save as PDF" produces a clean, one-slide-per-page deck presenters
 * can share offline. No external PDF library needed.
 *
 * IP rules: mirrors the demo screens exactly — no mechanism, no scores, no
 * workflow reveal. Only outcomes, illustrative data, and the wow moment.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Printer, ArrowLeft, CheckCircle2, Hospital, Sparkles, Stethoscope,
  Compass, MapPin, Radio, Gauge, Clock, LineChart, Eye, BadgeCheck,
} from 'lucide-react';
import { useDemo } from '../../contexts/DemoContext';
import { DEMO_META, ELEANOR_ARCHETYPES } from '../../data/demoData';

const PILLARS = [
  { icon: Compass, label: 'MATCH',   body: 'Appropriate post-acute options.' },
  { icon: MapPin,  label: 'PLACE',   body: 'A clearer path to confirmed placement.' },
  { icon: Radio,   label: 'CONNECT', body: 'Visibility across the transition.' },
  { icon: Gauge,   label: 'MEASURE', body: 'Actionable transition insight.' },
];

const OUTCOMES = [
  { icon: Clock,      title: 'Faster placement',       body: 'Less time between the referral and confirmed acceptance.' },
  { icon: LineChart,  title: 'Fewer manual touches',   body: 'Less time chasing status, calls, and faxes.' },
  { icon: Eye,        title: 'Greater visibility',     body: 'Case management sees where every discharge stands.' },
  { icon: BadgeCheck, title: 'Measurable performance', body: 'Transition metrics reported by unit and service line.' },
];

/* Injected print CSS. Kept in a <style> tag scoped to the deck route so
   the rest of the app's print behaviour is unchanged. */
const DECK_PRINT_STYLES = `
  @media print {
    @page { size: landscape; margin: 0; }
    body { background: white !important; }
    .deck-slide { page-break-after: always; break-after: page; height: 100vh; }
    .deck-slide:last-child { page-break-after: auto; break-after: auto; }
    .deck-toolbar { display: none !important; }
    /* Keep colors on print — presenters usually want the brand deck to
       look like the brand deck. */
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  }
  .deck-slide {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }
`;

export default function DemoDeck() {
  const { state, archetype } = useDemo();
  const navigate = useNavigate();
  const hospital = state?.hospital || {};
  const insights = state?.insights || {};
  const arche    = ELEANOR_ARCHETYPES[archetype] || ELEANOR_ARCHETYPES['home-health'];

  // Inject the print CSS once for the lifetime of the deck route.
  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-caraloom-demo-deck', '');
    style.appendChild(document.createTextNode(DECK_PRINT_STYLES));
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const doPrint = () => window.print();

  return (
    <div className="min-h-screen bg-white text-cn-ink-900" data-testid="demo-deck">
      {/* Toolbar (hidden in print) */}
      <div
        className="deck-toolbar sticky top-0 z-30 bg-white border-b border-cn-border px-4 md:px-6 h-14 flex items-center justify-between"
        data-testid="demo-deck-toolbar"
      >
        <button
          type="button"
          onClick={() => navigate('/demo/preview')}
          className="inline-flex items-center gap-1.5 text-sm text-cn-text-muted hover:text-cn-text"
          data-testid="demo-deck-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to demo
        </button>
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-cn-text-muted">
          <Sparkles className="w-3 h-3" /> CaraLoom Presenter Deck
        </div>
        <button
          type="button"
          onClick={doPrint}
          className="cn-btn cn-btn-primary text-sm"
          data-testid="demo-deck-print"
        >
          <Printer className="w-4 h-4" /> Download as PDF
        </button>
      </div>

      {/* Slide 1 — Title */}
      <Slide accent testId="deck-slide-title">
        <div className="text-[11px] uppercase tracking-widest text-white/70 font-mono">
          {DEMO_META.watermark}
        </div>
        <h1 className="mt-4 cn-display text-5xl md:text-7xl font-bold leading-[1.02] !text-white max-w-4xl">
          Every discharge, <span className="italic text-cn-accent">woven into one thread</span> of care.
        </h1>
        <p className="mt-8 text-2xl text-white/90 max-w-3xl">
          The post-acute placement and care-transition network.
        </p>
        <div className="mt-auto text-[11px] text-white/70 italic">
          Illustrative demo — {hospital.name}. Fictional data.
        </div>
      </Slide>

      {/* Slide 2 — Problem */}
      <Slide testId="deck-slide-problem">
        <div className="cn-eyebrow">The gap</div>
        <h2 className="mt-4 cn-display text-5xl md:text-6xl text-cn-ink-900 leading-tight">
          A referral isn&apos;t <span className="italic text-cn-primary">a placement.</span>
        </h2>
        <p className="mt-8 text-xl text-cn-text-muted max-w-3xl leading-relaxed">
          Hospitals have digitized referrals, but discharge teams still spend significant time
          chasing status, checking capacity, and trying to determine what happened after the
          patient left.
        </p>
        <div className="mt-10 cn-display text-3xl text-cn-primary">
          CaraLoom closes that gap.
        </div>
      </Slide>

      {/* Slide 3 — Where CaraLoom sits (patient journey) */}
      <Slide testId="deck-slide-journey">
        <div className="cn-eyebrow">Where CaraLoom sits</div>
        <h2 className="mt-4 cn-display text-5xl text-cn-ink-900 leading-tight">
          The connective layer between{' '}
          <span className="italic text-cn-text-muted">the hospital and post-acute care.</span>
        </h2>
        <div className="mt-16 grid grid-cols-3 gap-6 items-stretch max-w-5xl">
          {[
            { icon: Hospital,   title: 'Hospital',        body: 'Patient needs post-acute care.' },
            { icon: Sparkles,   title: 'CaraLoom',        body: 'Intelligent post-acute coordination.', emphasized: true },
            { icon: Stethoscope, title: 'Post-Acute Care', body: 'The next level of care begins.' },
          ].map((s) => (
            <div
              key={s.title}
              className={`rounded-3xl border p-8 text-center ${
                s.emphasized
                  ? 'border-cn-primary/40 bg-white shadow-lg'
                  : 'border-cn-border bg-cn-bone-100/60'
              }`}
            >
              <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center ${
                s.emphasized ? 'bg-cn-primary/10 text-cn-primary' : 'bg-cn-bone-200 text-cn-text-muted'
              }`}>
                <s.icon className="w-8 h-8" strokeWidth={1.6} />
              </div>
              <h3 className="mt-5 cn-display text-2xl text-cn-ink-900">{s.title}</h3>
              <p className="mt-2 text-sm text-cn-text-muted">{s.body}</p>
            </div>
          ))}
        </div>
      </Slide>

      {/* Slide 4 — MATCH · PLACE · CONNECT · MEASURE */}
      <Slide testId="deck-slide-pillars">
        <div className="cn-eyebrow">The platform</div>
        <h2 className="mt-4 cn-display text-5xl text-cn-ink-900 leading-tight max-w-4xl">
          Four outcomes that characterize CaraLoom.
        </h2>
        <div className="mt-12 grid grid-cols-2 gap-6 max-w-5xl">
          {PILLARS.map((p) => (
            <div key={p.label} className="rounded-2xl border border-cn-border bg-white p-8">
              <div className="w-12 h-12 rounded-2xl bg-cn-primary/10 text-cn-primary flex items-center justify-center">
                <p.icon className="w-6 h-6" strokeWidth={1.8} />
              </div>
              <h3 className="mt-6 cn-display text-3xl text-cn-ink-900 tracking-tight">{p.label}</h3>
              <p className="mt-2 text-base text-cn-text-muted">{p.body}</p>
            </div>
          ))}
        </div>
      </Slide>

      {/* Slide 5 — Eleanor's story (setup) */}
      <Slide testId="deck-slide-eleanor-setup">
        <div className="cn-eyebrow">A transition, in one thread</div>
        <h2 className="mt-4 cn-display text-5xl text-cn-ink-900 leading-tight max-w-4xl">
          Eleanor Carter, 78, needs post-acute care after discharge.
        </h2>
        <div className="mt-10 rounded-3xl border border-cn-border bg-cn-bone-100/60 p-8 max-w-3xl">
          <dl className="grid grid-cols-2 gap-y-4 gap-x-8 text-base">
            <dt className="text-cn-text-muted">Age</dt><dd className="text-cn-ink-900 font-semibold">78</dd>
            <dt className="text-cn-text-muted">Hospital</dt><dd className="text-cn-ink-900 font-semibold">{hospital.name}</dd>
            <dt className="text-cn-text-muted">Post-acute need</dt><dd className="text-cn-ink-900 font-semibold" data-testid="deck-eleanor-need">{arche.need}</dd>
            <dt className="text-cn-text-muted">Case management goal</dt><dd className="text-cn-ink-900 font-semibold">Confirmed placement, visible transition</dd>
          </dl>
        </div>
        <p className="mt-6 text-lg text-cn-text-muted italic">{arche.story}</p>
        <p className="mt-2 text-sm text-cn-text-muted italic">Fictional patient. Illustrative demo.</p>
      </Slide>

      {/* Slide 6 — Appropriate options */}
      <Slide testId="deck-slide-options">
        <div className="cn-eyebrow">CaraLoom identified appropriate options</div>
        <h2 className="mt-4 cn-display text-5xl text-cn-ink-900 leading-tight max-w-4xl">
          Appropriate Care Options for Eleanor Carter.
        </h2>
        <div className="mt-10 grid grid-cols-3 gap-4 max-w-5xl">
          {(state?.providers_by_type?.[arche.need] || []).slice(0, 3).map((p) => (
            <div key={p.id} className="rounded-2xl border border-cn-border bg-white p-6">
              <div className="text-[10px] uppercase tracking-widest text-cn-primary font-bold">
                Appropriate Option
              </div>
              <h3 className="mt-3 cn-display text-xl text-cn-ink-900">{p.name}</h3>
              <ul className="mt-4 text-xs text-cn-text-muted space-y-1">
                <li>Service Area: Available</li>
                <li>Care Type: {arche.need}</li>
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm text-cn-text-muted italic max-w-3xl">
          Options reflect the needs of this transition and available post-acute care information.
        </p>
      </Slide>

      {/* Slide 7 — WOW MOMENT: placement confirmed */}
      <Slide accent testId="deck-slide-wow">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/70 font-mono">
          <CheckCircle2 className="w-4 h-4 text-cn-accent" /> Placement Confirmed
        </div>
        <h2 className="mt-8 cn-display text-5xl md:text-6xl !text-white leading-[1.05] max-w-5xl">
          Eleanor Carter has a confirmed post-acute placement with{' '}
          <span className="italic text-cn-accent" data-testid="deck-eleanor-provider">{arche.provider}.</span>
        </h2>
        <div className="mt-10 text-2xl text-white/90">
          Expected care start: <span className="text-cn-accent font-semibold">{arche.expected}</span>
        </div>
        <div className="mt-16 border-l-4 border-cn-accent pl-6 text-2xl italic text-white/85 max-w-3xl">
          The referral isn&apos;t where visibility ends.
        </div>
      </Slide>

      {/* Slide 8 — Hospital ROI */}
      <Slide testId="deck-slide-roi">
        <div className="cn-eyebrow">For hospital leadership</div>
        <h2 className="mt-4 cn-display text-5xl text-cn-ink-900 leading-tight max-w-4xl">
          Better transitions aren&apos;t just better care.{' '}
          <span className="italic text-cn-text-muted">They&apos;re better operations.</span>
        </h2>
        <div className="mt-10 grid grid-cols-2 gap-4 max-w-5xl">
          {OUTCOMES.map((o) => (
            <div key={o.title} className="rounded-2xl border border-cn-border bg-white p-6">
              <div className="w-10 h-10 rounded-xl bg-cn-primary/10 text-cn-primary flex items-center justify-center">
                <o.icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <h3 className="mt-5 cn-display text-lg text-cn-ink-900">{o.title}</h3>
              <p className="mt-2 text-sm text-cn-text-muted">{o.body}</p>
            </div>
          ))}
        </div>
      </Slide>

      {/* Slide 9 — Illustrative analytics snapshot */}
      <Slide testId="deck-slide-insights">
        <div className="cn-eyebrow">Transition performance</div>
        <h2 className="mt-4 cn-display text-5xl text-cn-ink-900 leading-tight max-w-4xl">
          What leadership can measure with CaraLoom.
        </h2>
        <div className="mt-10 grid grid-cols-4 gap-4 max-w-5xl">
          {[
            { v: insights.median_time_to_placement || '1h 42m', l: 'Median Time to Placement' },
            { v: insights.confirmed_placement_pct || '78%',      l: 'Confirmed Placement' },
            { v: insights.transition_visibility_pct || '94%',    l: 'Transition Visibility' },
            { v: insights.needs_attention_pct || '6%',           l: 'Needs Attention' },
          ].map((k) => (
            <div key={k.l} className="rounded-2xl border border-cn-border bg-white p-6 text-center">
              <div className="cn-display text-3xl text-cn-primary tabular-nums">{k.v}</div>
              <div className="mt-2 text-[10px] uppercase tracking-widest text-cn-text-muted">{k.l}</div>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm text-cn-text-muted italic max-w-3xl">
          Illustrative demo data — not customer performance results.
        </p>
      </Slide>

      {/* Slide 10 — Pilot */}
      <Slide accent testId="deck-slide-pilot">
        <div className="text-[11px] uppercase tracking-widest text-white/70 font-mono">Hospital pilot</div>
        <h2 className="mt-4 cn-display text-5xl md:text-6xl !text-white leading-tight max-w-4xl">
          Start with one unit.{' '}
          <span className="italic text-cn-accent">Measure one transition.</span>
        </h2>
        <p className="mt-8 text-xl text-white/85 max-w-3xl leading-relaxed">
          Launch CaraLoom with a focused hospital pilot and measure what matters — for your
          service line, your case-management team, and your patients.
        </p>
        <div className="mt-12 grid grid-cols-2 gap-3 max-w-3xl text-white/90">
          {['Time to Placement', 'Transition Visibility', 'Manual Touches', 'Placement Outcomes', 'Care-Start Visibility'].map((m) => (
            <div key={m} className="flex items-center gap-3 rounded-xl bg-white/10 backdrop-blur-sm px-4 py-3 border border-white/15">
              <CheckCircle2 className="w-4 h-4 text-cn-accent" />
              <span className="text-sm">{m}</span>
            </div>
          ))}
        </div>
      </Slide>

      {/* Slide 11 — Final */}
      <Slide accent testId="deck-slide-final">
        <div className="text-[11px] uppercase tracking-widest text-white/70 font-mono">
          Close the transition
        </div>
        <h2 className="mt-4 cn-display text-5xl md:text-7xl !text-white leading-[1.02] max-w-5xl">
          Don&apos;t just send the referral.{' '}
          <span className="italic text-cn-accent">Close the transition.</span>
        </h2>
        <p className="mt-10 text-2xl text-white/85 max-w-3xl">
          CaraLoom connects hospitals and post-acute care through a more visible, coordinated transition.
        </p>
        <div className="mt-auto text-[11px] text-white/60 italic">
          © CaraLoom · Presenter Deck · {DEMO_META.watermark}
        </div>
      </Slide>
    </div>
  );
}

/* One deck slide — landscape orientation, full viewport height on screen,
   page-break-after in print. */
function Slide({ children, accent = false, testId }) {
  return (
    <section
      className={`deck-slide relative px-10 md:px-20 py-16 md:py-24 ${
        accent
          ? 'bg-gradient-to-br from-cn-primary via-cn-primary to-cn-secondary text-white'
          : 'bg-white text-cn-ink-900'
      }`}
      data-testid={testId}
    >
      {children}
      {accent && (
        <div
          className="pointer-events-none absolute top-0 right-0 w-[540px] h-[540px] rounded-full bg-cn-accent/10 blur-3xl"
          aria-hidden
        />
      )}
    </section>
  );
}
